
// src/pages/PainelCliente/PixClientePage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../axiosConfig";
import "./PixClientePage.css";

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

function formatBRL(n) {
  const v = Number(n || 0);
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDateTime(d) {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString("pt-BR");
}

function formatDate(d) {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("pt-BR");
}


function getApiErrorMessage(err, fallback = "Erro ao processar solicitação.") {
  return (
    err?.response?.data?.mensagem ||
    err?.response?.data?.error ||
    err?.response?.data?.erro ||
    err?.response?.data?.detalhe ||
    (typeof err?.response?.data === "string" ? err.response.data : "") ||
    err?.message ||
    fallback
  );
}

function maskPix(type, value) {
  const v = String(value || "").trim();
  if (!v) return "-";
  if (type === "EMAIL") {
    const [a, b] = v.split("@");
    if (!b) return v;
    return `${a.slice(0, 2)}***@${b}`;
  }
  const d = onlyDigits(v);
  if (d.length <= 6) return v;
  return `${d.slice(0, 2)}***${d.slice(-3)}`;
}

function badgeStatusOperacao(op) {
  const st = String(op.status_operacao || "").toLowerCase();
  if (st === "pendente") return { label: "Pendente", cls: "st-pendente" };
  if (st === "aprovada") return { label: "Aprovada", cls: "st-aprovada" };
  if (st === "rejeitada") return { label: "Rejeitada", cls: "st-rejeitada" };
  if (st === "nao_elegivel") return { label: "Não elegível", cls: "st-nao-elegivel" };
  return { label: op.status_operacao || "—", cls: "st-outro" };
}

function badgePagamento(op) {
  const stOp = String(op.status_operacao || "").toLowerCase();
  const stPg = String(op.status_pagamento || "").toLowerCase();

  // ✅ regra: não elegível NÃO tem pagamento
  if (stOp === "nao_elegivel") return { label: "Sem pagamento", cls: "pg-na" };
  if (stOp === "rejeitada") return { label: "Sem pagamento", cls: "pg-na" };

  if (stPg === "pago") return { label: "Pago", cls: "pg-pago" };
  if (stPg === "aguardando") return { label: "Aguardando", cls: "pg-aguardando" };
  return { label: op.status_pagamento || "—", cls: "pg-outro" };
}

function withinDateRange(dateStr, ini, fim) {
  if (!ini && !fim) return true;
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;

  if (ini) {
    const i = new Date(`${ini}T00:00:00`);
    if (d < i) return false;
  }
  if (fim) {
    const f = new Date(`${fim}T23:59:59`);
    if (d > f) return false;
  }
  return true;
}

export default function PixClientePage() {
  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("usuario")) || {};
    } catch {
      return {};
    }
  }, []);

  const userId = Number(usuario?.id);

  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);

  const [pixEmpresaAtiva, setPixEmpresaAtiva] = useState(false);
  const [empresaPixVerificada, setEmpresaPixVerificada] = useState(false);

  const [operacoes, setOperacoes] = useState([]);
  const [conta, setConta] = useState([]);

  const [form, setForm] = useState({
    numero_pedido: "",
    pix_chave_tipo: "CPF",
    pix_chave_valor: "",
  });
  
  // ✅ chave do localStorage (por usuário)
  const PIX_LS_KEY = useMemo(() => `pix_premiado_chave_v1:${userId || "anon"}`, [userId]);

  // ✅ carrega chave salva ao iniciar (ou quando userId ficar disponível)
  useEffect(() => {
    if (!userId) return;
    try {
      const raw = localStorage.getItem(PIX_LS_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved) return;

      const tipo = String(saved.pix_chave_tipo || "").trim();
      const valor = String(saved.pix_chave_valor || "").trim();
      if (!tipo || !valor) return;

      setForm((p) => ({
        ...p,
        pix_chave_tipo: tipo,
        pix_chave_valor: valor,
      }));
    } catch {
      // ignora
    }
  }, [userId, PIX_LS_KEY]);

  // ✅ salva chave sempre que o usuário altera (não salva pedido)
  useEffect(() => {
   if (!userId) return;
   try {
      const tipo = String(form.pix_chave_tipo || "").trim();
      const valor = String(form.pix_chave_valor || "").trim();
      if (!tipo || !valor) return; // só salva quando estiver preenchido

      localStorage.setItem(
       PIX_LS_KEY,
        JSON.stringify({
          pix_chave_tipo: tipo,
          pix_chave_valor: valor,
          updated_at: new Date().toISOString(),
        })
      );
    } catch {
      // ignora
    }
  }, [userId, PIX_LS_KEY, form.pix_chave_tipo, form.pix_chave_valor]);

  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null); // {type:'ok'|'err', text:''}

  // modal preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ✅ filtros do extrato
  const [filters, setFilters] = useState({
    data_ini: "",
    data_fim: "",
    status_operacao: "todas", // todas | pendente | aprovada | rejeitada | nao_elegivel
    status_pagamento: "todas", // todas | aguardando | pago | sem_pagamento
    pedido: "",
  });

  // ✅ paginação
  const [pageOps, setPageOps] = useState(1);
  const [pageCc, setPageCc] = useState(1);
  const OPS_PER_PAGE = 8;
  const CC_PER_PAGE = 10;

  // ✅ "Saldo Pix" pro cliente = quanto falta receber (aprovadas + aguardando).
  // Como a conta-corrente é consolidada (1 linha por pedido), não dá pra calcular crédito - pagamento sem distorcer.


  const loadAll = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const [cfgRes, opsRes, ccRes, clienteRes] = await Promise.all([
        axios.get("/pix/config"),
        axios.get(`/pix/operacoes/cliente/${userId}`),
        axios.get(`/pix/conta-corrente/cliente/${userId}`),
        axios.get(`/clientes/${userId}`),
      ]);

      setCfg(cfgRes.data || null);
      setOperacoes(Array.isArray(opsRes.data) ? opsRes.data : []);
      setConta(Array.isArray(ccRes.data) ? ccRes.data : []);

      const cnpjEmpresa = clienteRes.data?.cnpj_empresa;

      if (!cnpjEmpresa) {
        setPixEmpresaAtiva(false);
        setEmpresaPixVerificada(true);
        return;
      }

      const cnpjLimpo = String(cnpjEmpresa).replace(/\D/g, "");
      const empresaRes = await axios.get(`/empresas/cnpj/${cnpjLimpo}`);
      const empresa = empresaRes.data;

      setPixEmpresaAtiva(Number(empresa?.pix_ativa ?? 1) === 1);
      setEmpresaPixVerificada(true);
    } catch (e) {
        setPixEmpresaAtiva(false);
        setEmpresaPixVerificada(true);

        const detalhe =
          e?.response?.data?.error || e?.message || "Erro ao carregar Pix Premiado.";
        setMsg({ type: "err", text: detalhe });
      } finally {
        setLoading(false);
      }
    }, [userId]);

  useEffect(() => {
    if (!userId) return;
    loadAll();
  }, [userId, loadAll]);

  const validarForm = () => {
    const pedido = onlyDigits(form.numero_pedido);
    if (!pedido) return "Informe o número do pedido.";
    if (!form.pix_chave_tipo) return "Selecione o tipo da chave PIX.";
    const chave = String(form.pix_chave_valor || "").trim();
    if (!chave) return "Informe a chave PIX.";
    if (form.pix_chave_tipo === "CPF" && onlyDigits(chave).length !== 11)
      return "CPF inválido.";
    if (form.pix_chave_tipo === "CNPJ" && onlyDigits(chave).length !== 14)
      return "CNPJ inválido.";
    if (form.pix_chave_tipo === "TELEFONE" && onlyDigits(chave).length < 10)
      return "Telefone inválido.";
    if (form.pix_chave_tipo === "EMAIL" && !chave.includes("@"))
      return "Email inválido.";
    return null;
  };

  const openPreview = async (e) => {
    e.preventDefault();
    setMsg(null);

    if (empresaPixVerificada && !pixEmpresaAtiva) {
      setMsg({
        type: "err",
        text: "Atualmente, sua empresa não está participando do Pix Premiado. Por isso, não é possível cadastrar operações nesta promoção.",
      });
      return;
    }

    const erro = validarForm();
    if (erro) {
      setMsg({ type: "err", text: erro });
      return;
    }

    setPreviewLoading(true);
    try {
      const payload = {
        usuario_id: userId,
        numero_pedido: onlyDigits(form.numero_pedido),
        pix_chave_tipo: form.pix_chave_tipo,
        pix_chave_valor: String(form.pix_chave_valor || "").trim(),
      };

      const res = await axios.post("/pix/operacoes/preview", payload);
      setPreview(res.data?.resumo || null);
      setPreviewOpen(true);
    } catch (e2) {
      const detalhe = getApiErrorMessage(
        e2,
        "Erro ao gerar preview da operação Pix."
      );
      setMsg({ type: "err", text: detalhe });
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmSubmit = async () => {
    if (!preview) return;
    setSubmitting(true);
    setMsg(null);

    try {
      const payload = {
        usuario_id: userId,
        numero_pedido: onlyDigits(form.numero_pedido),
        pix_chave_tipo: form.pix_chave_tipo,
        pix_chave_valor: String(form.pix_chave_valor || "").trim(),
      };

      const res = await axios.post("/pix/operacoes", payload);

      const elegivel = !!res.data?.elegivel;
      const pixVal = res.data?.pix_valor;

      setMsg({
        type: "ok",
        text: elegivel
          ? `Operação enviada para aprovação. PIX previsto: ${formatBRL(pixVal)}`
          : "Operação registrada, mas não elegível (mínimo 3 parcelas e R$ 1.500).",
      });

      // ✅ limpa só o pedido para cadastrar várias operações seguidas
      setForm((p) => ({ ...p, numero_pedido: "" }));
      setPreviewOpen(false);
      setPreview(null);

      await loadAll();
    } catch (e2) {
      const detalhe = getApiErrorMessage(
        e2,
        "Erro ao cadastrar operação Pix."
      );
      setMsg({ type: "err", text: detalhe });
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ filtro + ordenação (extrato por pedido)
  const operacoesFiltradas = useMemo(() => {
    const pedidoFiltro = String(filters.pedido || "").trim();
    const stOp = String(filters.status_operacao || "todas");
    const stPg = String(filters.status_pagamento || "todas");

    const arr = [...operacoes];

    // ordena por criado_em desc (mais recente primeiro)
    arr.sort((a, b) => new Date(b.criado_em) - new Date(a.criado_em));

    return arr.filter((op) => {
      const okPeriodo = withinDateRange(op.data_operacao || op.criado_em, filters.data_ini, filters.data_fim);

      const okPedido = !pedidoFiltro
        ? true
        : String(op.numero_pedido || "").includes(pedidoFiltro);

      const opStatus = String(op.status_operacao || "").toLowerCase();

      const okStatusOperacao =
        stOp === "todas" ? true : opStatus === stOp;

      // status pagamento “virtual”
      const pagBadge = badgePagamento(op);
      const pagLabel = pagBadge.label; // "Pago" / "Aguardando" / "Sem pagamento"
      const okStatusPagamento =
        stPg === "todas"
          ? true
          : stPg === "sem_pagamento"
          ? pagLabel === "Sem pagamento"
          : stPg === "pago"
          ? pagLabel === "Pago"
          : stPg === "aguardando"
          ? pagLabel === "Aguardando"
          : true;

      return okPeriodo && okPedido && okStatusOperacao && okStatusPagamento;
    });
  }, [operacoes, filters]);

  // ✅ totals (baseado nas operações filtradas)
  const totals = useMemo(() => {
    const t = {
      total_previsto: 0,   // soma pix_valor das aprovadas
      a_receber: 0,        // aprovadas + aguardando
      recebidos: 0,        // aprovadas + pago
      qtd_aprovadas: 0,
      qtd_pendentes: 0,
      qtd_rejeitadas: 0,
      qtd_nao_elegivel: 0,
    };

    for (const op of operacoesFiltradas) {
      const so = String(op.status_operacao || "").toLowerCase();
      const sp = String(op.status_pagamento || "").toLowerCase();

      if (so === "pendente") t.qtd_pendentes += 1;
      if (so === "rejeitada") t.qtd_rejeitadas += 1;
      if (so === "nao_elegivel") t.qtd_nao_elegivel += 1;

      if (so === "aprovada") {
        t.qtd_aprovadas += 1;
        t.total_previsto += Number(op.pix_valor || 0);
        if (sp === "pago") t.recebidos += Number(op.pix_valor || 0);
        if (sp === "aguardando") t.a_receber += Number(op.pix_valor || 0);
      }
    }
    return t;
  }, [operacoesFiltradas]);

  // ✅ "Saldo Pix" pro cliente = quanto falta receber (aprovadas + aguardando)
  const saldoPix = Number(totals?.a_receber || 0);

  // ✅ paginação extrato
  const totalPagesOps = useMemo(() => {
    return Math.max(1, Math.ceil(operacoesFiltradas.length / OPS_PER_PAGE));
  }, [operacoesFiltradas.length]);

  const opsPageItems = useMemo(() => {
    const p = Math.min(Math.max(1, pageOps), totalPagesOps);
    const start = (p - 1) * OPS_PER_PAGE;
    return operacoesFiltradas.slice(start, start + OPS_PER_PAGE);
  }, [operacoesFiltradas, pageOps, totalPagesOps]);

  // ✅ paginação conta corrente
  const contaSorted = useMemo(() => {
    const arr = [...conta];
    arr.sort((a, b) => new Date(b.criado_em) - new Date(a.criado_em));
    return arr;
  }, [conta]);

  const totalPagesCc = useMemo(() => {
    return Math.max(1, Math.ceil(contaSorted.length / CC_PER_PAGE));
  }, [contaSorted.length]);

  const ccPageItems = useMemo(() => {
    const p = Math.min(Math.max(1, pageCc), totalPagesCc);
    const start = (p - 1) * CC_PER_PAGE;
    return contaSorted.slice(start, start + CC_PER_PAGE);
  }, [contaSorted, pageCc, totalPagesCc]);

  // se filtros mudarem, volta pra página 1
  useEffect(() => {
    setPageOps(1);
  }, [filters.data_ini, filters.data_fim, filters.status_operacao, filters.status_pagamento, filters.pedido]);

  const limparFiltros = () => {
    setFilters({
      data_ini: "",
      data_fim: "",
      status_operacao: "todas",
      status_pagamento: "todas",
      pedido: "",
    });
  };

  return (
    <div className="pixc-page">
      <div className="pixc-container">
        <div className="pixc-header">
          <div>
            <h2>Pix Premiado</h2>
            <p className="pixc-sub">
              Cadastre seu pedido para análise. Se aprovado, o PIX será pago conforme a faixa da promoção.
            </p>
          </div>

          <div className="pixc-saldo">
            <div className="pixc-saldo-label">Saldo Pix</div>
            <div className="pixc-saldo-valor">{formatBRL(saldoPix)}</div>
            <div className="pixc-saldo-mini">(valor a receber)</div>
          </div>
        </div>

        {/* ✅ TOTALIZADORES */}
        <div className="pixc-totals">
          <div className="pixc-total-card">
            <div className="pixc-total-label">Recebidos</div>
            <div className="pixc-total-value">{formatBRL(totals.recebidos)}</div>
            <div className="pixc-total-sub">Aprovadas e pagas</div>
          </div>

          <div className="pixc-total-card highlight">
            <div className="pixc-total-label">A receber</div>
            <div className="pixc-total-value">{formatBRL(totals.a_receber)}</div>
            <div className="pixc-total-sub">Aprovadas aguardando</div>
          </div>

          <div className="pixc-total-card">
            <div className="pixc-total-label">Total Geral</div>
            <div className="pixc-total-value">{formatBRL(totals.total_previsto)}</div>
            <div className="pixc-total-sub">Soma de PIX das aprovadas</div>
          </div>

          <div className="pixc-total-card">
            <div className="pixc-total-label">Operações</div>
            <div className="pixc-total-value">
              {totals.qtd_aprovadas + totals.qtd_pendentes + totals.qtd_rejeitadas + totals.qtd_nao_elegivel}
            </div>
            <div className="pixc-total-sub">
              Pendentes: <strong>{totals.qtd_pendentes}</strong> • Aprovadas: <strong>{totals.qtd_aprovadas}</strong> • Não elegíveis: <strong>{totals.qtd_nao_elegivel}</strong>
            </div>
          </div>
        </div>

        {/* regras */}
        <div className="pixc-rules">
          <div className="pixc-rules-title">Dinheiro na mão 2026 — mais fácil de ganhar!</div>
          <div className="pixc-rules-sub">Cadastre aqui as suas operações</div>

          <p className="pixc-rules-text">
            Participar é simples: para cada operação aprovada na Tikpag, preencha o formulário abaixo e envie para análise.
            Os dados serão computados até o último dia útil do mês, e os prêmios serão pagos até o dia 10 do mês seguinte.
          </p>

          <div className="pixc-rules-box">
            <div className="pixc-rules-box-title">Regras da promoção</div>
            <ul>
              <li>Promoção válida para operações com parcelamento em <strong>03 vezes ou mais</strong>.</li>
              <li>Operações abaixo de <strong>R$ 1.500</strong> não são elegíveis.</li>
              <li>Somente <strong>01 cadastro por operação</strong> (por número de pedido).</li>
              <li>Cadastros retroativos ao mês vigente podem ser rejeitados.</li>
              <li>Ao cadastrar, sua operação fica <strong>pendente</strong> até análise do administrador.</li>
              <li>Se aprovada, o PIX será pago para a chave informada.</li>
            </ul>

            <div className="pixc-tiers">
              <div className="pixc-tier">
                <div className="pixc-tier-top">Acima de R$ 1.500</div>
                <div className="pixc-tier-val">PIX de R$ 50</div>
              </div>
              <div className="pixc-tier">
                <div className="pixc-tier-top">Acima de R$ 3.000</div>
                <div className="pixc-tier-val">PIX de R$ 75</div>
              </div>
              <div className="pixc-tier">
                <div className="pixc-tier-top">Acima de R$ 5.000</div>
                <div className="pixc-tier-val">PIX de R$ 100</div>
              </div>
              <div className="pixc-tier">
                <div className="pixc-tier-top">Acima de R$ 7.500</div>
                <div className="pixc-tier-val">PIX de R$ 150</div>
              </div>
            </div>

            <div className="pixc-rules-foot">Promoção válida por tempo indeterminado.</div>
          </div>
        </div>

        {cfg?.banner && (
          <div className="pixc-banner">
            <strong>ℹ️</strong>
            <span>{cfg.banner}</span>
          </div>
        )}

        {empresaPixVerificada && !pixEmpresaAtiva && (
          <div className="pixc-msg err">
            <strong>Atualmente, sua empresa não está participando do Pix Premiado. Você ainda pode consultar seu extrato, mas não é possível cadastrar novas operações nesta promoção./strong</strong>
          </div>
        )}

        {msg && (
          <div className={`pixc-msg ${msg.type === "ok" ? "ok" : "err"}`}>
            {msg.text}
          </div>
        )}

        <div className="pixc-grid">
          {/* cadastrar */}
          <section className="pixc-card">
            <h3>Cadastrar pedido</h3>

            <form className="pixc-form" onSubmit={openPreview}>
              <div className="pixc-row">
                <label>Número do pedido</label>
                <input
                  value={form.numero_pedido}
                  onChange={(e) => setForm((p) => ({ ...p, numero_pedido: e.target.value }))}
                  placeholder="Ex: 221"
                  inputMode="numeric"
                  autoComplete="off"
                />
              </div>

              <div className="pixc-row">
                <label>Tipo de chave</label>
                <select
                  value={form.pix_chave_tipo}
                  onChange={(e) => setForm((p) => ({ ...p, pix_chave_tipo: e.target.value }))}
                >
                  <option value="CPF">CPF</option>
                  <option value="CNPJ">CNPJ</option>
                  <option value="EMAIL">Email</option>
                  <option value="TELEFONE">Telefone</option>
                  <option value="ALEATORIA">Chave aleatória</option>
                </select>
              </div>

              <div className="pixc-row">
                <label>Chave PIX</label>
                <input
                  value={form.pix_chave_valor}
                  onChange={(e) => setForm((p) => ({ ...p, pix_chave_valor: e.target.value }))}
                  placeholder={
                    form.pix_chave_tipo === "EMAIL"
                      ? "email@exemplo.com"
                      : form.pix_chave_tipo === "TELEFONE"
                      ? "11999999999"
                      : form.pix_chave_tipo === "ALEATORIA"
                      ? "Chave aleatória"
                      : "Somente números"
                  }
                  inputMode={form.pix_chave_tipo === "EMAIL" ? "email" : "text"}
                  autoComplete="off"
                />
              </div>

              <button type="submit" disabled={submitting || previewLoading || !pixEmpresaAtiva}>
                {previewLoading ? "Carregando resumo..." : "Continuar"}
              </button>
              <button
                  type="button"
                  className="pixc-btn ghost"
                  onClick={() => {
                    localStorage.removeItem(PIX_LS_KEY);
                    setForm((p) => ({ ...p, pix_chave_valor: "" }));
                  }}
                >
                  Limpar chave salva
                </button>

              <div className="pixc-hint">
                Você verá um <strong>resumo</strong> para confirmar antes de enviar para análise.
              </div>
            </form>
          </section>

          {/* filtros + extrato */}
          <section className="pixc-card">
            <div className="pixc-card-head">
              <h3>Extrato Pix (por pedido)</h3>
              <div className="pixc-chip">{operacoesFiltradas.length} registro(s)</div>
            </div>

            <form
              className="pixc-filters"
              onSubmit={(e) => {
                e.preventDefault();
                setPageOps(1);
              }}
            >
              <div className="pixc-filter-row">
                <div className="pixc-filter">
                  <label>Data inicial</label>
                  <input
                    type="date"
                    value={filters.data_ini}
                    onChange={(e) => setFilters((p) => ({ ...p, data_ini: e.target.value }))}
                  />
                </div>
                <div className="pixc-filter">
                  <label>Data final</label>
                  <input
                    type="date"
                    value={filters.data_fim}
                    onChange={(e) => setFilters((p) => ({ ...p, data_fim: e.target.value }))}
                  />
                </div>
              </div>

              <div className="pixc-filter-row">
                <div className="pixc-filter">
                  <label>Status</label>
                  <select
                    value={filters.status_operacao}
                    onChange={(e) => setFilters((p) => ({ ...p, status_operacao: e.target.value }))}
                  >
                    <option value="todas">Todos</option>
                    <option value="pendente">Pendente</option>
                    <option value="aprovada">Aprovada</option>
                    <option value="rejeitada">Rejeitada</option>
                    <option value="nao_elegivel">Não elegível</option>
                  </select>
                </div>

                <div className="pixc-filter">
                  <label>Pagamento</label>
                  <select
                    value={filters.status_pagamento}
                    onChange={(e) => setFilters((p) => ({ ...p, status_pagamento: e.target.value }))}
                  >
                    <option value="todas">Todos</option>
                    <option value="aguardando">Aguardando</option>
                    <option value="pago">Pago</option>
                    <option value="sem_pagamento">Sem pagamento</option>
                  </select>
                </div>
              </div>

              <div className="pixc-filter-row">
                <div className="pixc-filter wide">
                  <label>Pedido</label>
                  <input
                    value={filters.pedido}
                    onChange={(e) => setFilters((p) => ({ ...p, pedido: e.target.value }))}
                    placeholder="Ex: 221"
                  />
                </div>

                <div className="pixc-filter-actions">
                  <button type="button" className="pixc-btn ghost" onClick={limparFiltros}>
                    Limpar
                  </button>
                </div>
              </div>
            </form>

            {loading ? (
              <div className="pixc-loading">Carregando...</div>
            ) : operacoesFiltradas.length === 0 ? (
              <div className="pixc-empty">Nenhuma operação encontrada com esses filtros.</div>
            ) : (
              <>
                <div className="pixc-table-wrap">
                  <table className="pixc-table">
                    <thead>
                      <tr>
                        <th>Pedido</th>
                        <th>Valor</th>
                        <th>Parcelas</th>
                        <th>PIX</th>
                        <th>Status</th>
                        <th>Pagamento</th>
                        <th>Cadastro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opsPageItems.map((op) => {
                        const b = badgeStatusOperacao(op);
                        const pg = badgePagamento(op);
                        const showMotivo =
                          String(op.status_operacao || "").toLowerCase() === "rejeitada" &&
                          String(op.motivo_rejeicao || "").trim();

                        return (
                          <React.Fragment key={op.id}>
                            <tr>
                              <td>{op.numero_pedido}</td>
                              <td>{formatBRL(op.valor)}</td>
                              <td>{op.numero_parcelas}x</td>
                              <td>{formatBRL(op.pix_valor)}</td>
                              <td>
                                <span className={`pixc-badge ${b.cls}`}>{b.label}</span>
                              </td>
                              <td>
                                <span className={`pixc-pill ${pg.cls}`}>{pg.label}</span>
                              </td>
                              <td>{formatDateTime(op.criado_em)}</td>
                            </tr>

                            {showMotivo && (
                              <tr className="pixc-motivo-row">
                                <td colSpan={7}>
                                  <div className="pixc-motivo">
                                    <strong>Motivo da rejeição:</strong> {String(op.motivo_rejeicao).trim()}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* paginação */}
                <div className="pixc-pagination">
                  <button
                    className="pixc-btn ghost"
                    onClick={() => setPageOps((p) => Math.max(1, p - 1))}
                    disabled={pageOps <= 1}
                  >
                    ◀
                  </button>

                  <div className="pixc-pageinfo">
                    Página <strong>{pageOps}</strong> de <strong>{totalPagesOps}</strong>
                  </div>

                  <button
                    className="pixc-btn ghost"
                    onClick={() => setPageOps((p) => Math.min(totalPagesOps, p + 1))}
                    disabled={pageOps >= totalPagesOps}
                  >
                    ▶
                  </button>
                </div>
              </>
            )}
          </section>

          {/* conta corrente */}
          <section className="pixc-card pixc-card-full">
            <div className="pixc-card-head">
              <h3>Conta corrente</h3>
              <div className="pixc-chip">{contaSorted.length} lançamento(s)</div>
            </div>

            {loading ? (
              <div className="pixc-loading">Carregando...</div>
            ) : contaSorted.length === 0 ? (
              <div className="pixc-empty">Sem lançamentos ainda.</div>
            ) : (
              <>
                <div className="pixc-table-wrap">
                  <table className="pixc-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Valor</th>
                        <th>Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ccPageItems.map((l) => (
                        <tr key={l.id}>
                          <td>{formatDateTime(l.criado_em)}</td>
                          <td>
                            <span className={`pixc-badge ${l.tipo === "credito" ? "st-aprovada" : "st-rejeitada"}`}>
                              {l.tipo === "credito" ? "Crédito" : "Pagamento"}
                            </span>
                          </td>
                          <td>{formatBRL(l.valor)}</td>
                          <td>{l.descricao || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pixc-pagination">
                  <button
                    className="pixc-btn ghost"
                    onClick={() => setPageCc((p) => Math.max(1, p - 1))}
                    disabled={pageCc <= 1}
                  >
                    ◀
                  </button>

                  <div className="pixc-pageinfo">
                    Página <strong>{pageCc}</strong> de <strong>{totalPagesCc}</strong>
                  </div>

                  <button
                    className="pixc-btn ghost"
                    onClick={() => setPageCc((p) => Math.min(totalPagesCc, p + 1))}
                    disabled={pageCc >= totalPagesCc}
                  >
                    ▶
                  </button>
                </div>
              </>
            )}
          </section>
        </div>

        {/* Modal Preview */}
        {previewOpen && (
          <div className="pixc-modal-backdrop" onClick={() => !submitting && setPreviewOpen(false)}>
            <div className="pixc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pixc-modal-head">
                <div>
                  <div className="pixc-modal-title">Confirme os dados</div>
                  <div className="pixc-modal-sub">Revise antes de enviar para análise.</div>
                </div>
                <button
                  className="pixc-modal-x"
                  onClick={() => !submitting && setPreviewOpen(false)}
                  disabled={submitting}
                  title="Fechar"
                >
                  ✕
                </button>
              </div>

              <div className="pixc-modal-body">
                <div className="pixc-summary">
                  <div className="pixc-sum-item">
                    <span>Pedido</span>
                    <strong>{preview?.numero_pedido || onlyDigits(form.numero_pedido)}</strong>
                  </div>
                  <div className="pixc-sum-item">
                    <span>Data</span>
                    <strong>{preview?.data_operacao || "-"}</strong>
                  </div>
                  <div className="pixc-sum-item">
                    <span>Valor</span>
                    <strong>{formatBRL(preview?.valor)}</strong>
                  </div>
                  <div className="pixc-sum-item">
                    <span>Parcelas</span>
                    <strong>{preview?.numero_parcelas || 1}x</strong>
                  </div>
                  <div className="pixc-sum-item">
                    <span>PIX previsto</span>
                    <strong>{formatBRL(preview?.pix_valor)}</strong>
                  </div>
                  <div className="pixc-sum-item">
                    <span>Pagador</span>
                    <strong>{preview?.nome_pagador || "-"}</strong>
                  </div>
                  <div className="pixc-sum-item">
                    <span>Chave</span>
                    <strong>
                      {preview?.pix_chave_tipo || form.pix_chave_tipo} •{" "}
                      {maskPix(
                        preview?.pix_chave_tipo || form.pix_chave_tipo,
                        preview?.pix_chave_valor || form.pix_chave_valor
                      )}
                    </strong>
                  </div>
                  <div className="pixc-sum-item">
                    <span>Elegível</span>
                    <strong>{preview?.elegivel ? "Sim" : "Não"}</strong>
                  </div>
                </div>

                {!preview?.elegivel && (
                  <div className="pixc-modal-warn">
                    ⚠️ Essa operação pode ser registrada como <strong>não elegível</strong> (mínimo 3 parcelas e R$ 1.500).
                  </div>
                )}
              </div>

              <div className="pixc-modal-foot">
                <button className="pixc-btn ghost" onClick={() => setPreviewOpen(false)} disabled={submitting}>
                  Voltar
                </button>
                <button className="pixc-btn primary" onClick={confirmSubmit} disabled={submitting}>
                  {submitting ? "Enviando..." : "Confirmar e enviar"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}